import {useEffect} from 'react';
import {useQueryClient} from '@tanstack/react-query';

import {subscribeToChannel} from '@shared/realtime';
import {
  QUERY__CHAT_TOPIC_CHANNEL,
  QUERY__CHAT_TOPIC_MESSAGES,
} from '../queryKeys';

type ChatPublicationType =
  | 'message_created'
  | 'message_deleted'
  | 'channel_status_changed';

interface ChatPublication {
  type: ChatPublicationType;
  data: unknown;
}

const isChatPublication = (value: unknown): value is ChatPublication => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;

  const type = (value as {type?: unknown}).type;
  return (
    type === 'message_created' ||
    type === 'message_deleted' ||
    type === 'channel_status_changed'
  );
};

export const useTopicChatUpdates = (
  topicSlug: string,
  channelId: string | undefined,
) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!topicSlug || !channelId) return;

    return subscribeToChannel(`public:chat:${channelId}`, publication => {
      if (!isChatPublication(publication)) {
        console.error(
          '[Centrifugo] invalid topic chat publication',
          publication,
        );
        return;
      }

      if (
        publication.type === 'message_created' ||
        publication.type === 'message_deleted'
      ) {
        void queryClient.invalidateQueries({
          queryKey: [QUERY__CHAT_TOPIC_MESSAGES, {topicSlug}],
        });
        return;
      }

      void queryClient.invalidateQueries({
        queryKey: [QUERY__CHAT_TOPIC_CHANNEL, topicSlug],
      });
    });
  }, [channelId, queryClient, topicSlug]);
};
